import React, { Suspense, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import {
  CLUB_COURSE_MAPPING_SELECT,
  GOLF_CLUB_SELECT_PAGE,
  GOLF_COURSE_SELECT_NO_ABOUT,
} from '../../../constants/golfCoursePageColumns';
import { AuthContext } from '../../../context/AuthContext';
import { supabase } from '../../../utils/supabase';
import { slugToLabel } from '../../../utils/slugToLabel';
import NotFound from '../../error/NotFound';
import LoadingScreen from '../../loading/LoadingScreen';
import JsonLd from '../../meta/JsonLd';
import MetaTags from '../../meta/MetaTags';
import GolfCourseAbout from './GolfCourseAbout';
import GolfCourseDesign from './GolfCourseDesign';
import GolfCourseHeader from './GolfCourseHeader';
import GolfCourseNav from './GolfCourseNav';
import GolfCoursePar from './GolfCoursePar';
import GolfCourseRank from './GolfCourseRank';
import ContributeBanner from '../golfClub/ContributeBanner';

// Lazy: keeps main bundle smaller (parse/compile). Skeleton lets header + rank paint first.
const CourseImageCarousel = React.lazy(() =>
  import(
    /* webpackChunkName: "course-image-carousel" */
    /* webpackPrefetch: true */
    './CourseImageCarousel'
  ),
);
const SingleCourseMap = React.lazy(() => import('../../maps/SingleCourseMap'));
const VisitorInfo = React.lazy(() => import('./VisitorInfo'));
const GolfCourseGreenFees = React.lazy(() => import('./GolfCourseGreenFees'));

function getRankFieldForCountry(courseCountry) {
  if (!courseCountry) return 'england_rank_gl';
  switch (String(courseCountry).toLowerCase()) {
    case 'england':
      return 'england_rank_gl';
    case 'scotland':
      return 'scotland_rank_gl';
    case 'wales':
      return 'wales_rank_gl';
    case 'ireland':
      return 'ireland_rank_gl';
    case 'northern ireland':
      return 'ni_rank_gl';
    case 'usa':
      return 'usa_rank_gl';
    default:
      return 'england_rank_gl';
  }
}

function CourseMapSkeleton() {
  return (
    <div
      className="max-h-[340px] lg:max-h-[345px] single-leaflet-container shadow-xl rounded-xl bg-gray-100 p-8 mt-1 flex min-h-[200px] items-center justify-center text-gray-500 text-sm"
      aria-busy="true"
      aria-live="polite"
    >
      Loading map…
    </div>
  );
}

/** Matches gallery min-heights — avoid full-page spinner while carousel chunk loads. */
function CourseCarouselSkeleton() {
  return (
    <div
      className="relative h-[250px] w-full md:h-[350px] lg:h-[492px] rounded-lg bg-gray-200/90 animate-pulse"
      aria-hidden
    />
  );
}

function stripHtmlForMeta(html) {
  if (!html || typeof html !== 'string') return '';
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

/** Shown while the primary golf_courses row loads — keeps nav + layout, avoids full-page spinner. */
function GolfCourseShellSkeleton({ label }) {
  return (
    <div className="pb-12" aria-busy="true" aria-live="polite">
      <div className="mb-6 xl:mb-8">
        <div className="h-9 sm:h-10 w-11/12 max-w-xl bg-gray-300/90 rounded-md animate-pulse mb-3" />
        <div className="h-4 w-2/3 max-w-md bg-gray-200 rounded animate-pulse" />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-3 xl:gap-4 gap-6">
        <div className="space-y-4 min-h-[280px]">
          <div className="h-52 bg-gray-200/90 rounded-lg animate-pulse" />
          <div className="h-36 bg-gray-200/80 rounded-lg animate-pulse" />
          <div className="h-28 bg-gray-200/80 rounded-lg animate-pulse" />
        </div>
        <div className="space-y-4 min-h-[200px] hidden xl:block">
          <div className="h-40 bg-gray-200/80 rounded-lg animate-pulse" />
          <div className="h-48 bg-gray-200/80 rounded-lg animate-pulse" />
        </div>
        <div className="space-y-4 min-h-[200px] hidden xl:block">
          <div className="h-44 bg-gray-200/80 rounded-lg animate-pulse" />
          <div className="h-52 bg-gray-200/80 rounded-lg animate-pulse" />
        </div>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 xl:gap-4 gap-6 mt-8">
        <div className="h-56 bg-gray-200/70 rounded-lg animate-pulse" />
        <div className="h-56 bg-gray-200/70 rounded-lg animate-pulse" />
      </div>
      <p className="mt-10 text-sm text-gray-500 text-center">
        Loading {label}…
      </p>
    </div>
  );
}

const GolfCourseOverview = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { courseSlug } = useParams();
  const [course, setCourse] = useState(null);
  const [clubCourseMapping, setClubCourseMapping] = useState(null);
  const [greenFees, setGreenFees] = useState(null);
  const [reciprocalsClean, setReciprocalsClean] = useState(null);
  const [courseImages, setCourseImages] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [golfClubs, setGolfClubs] = useState(null);
  const { user, editableCourses, editPermissions, username } =
    useContext(AuthContext);
  const [activeSection, setActiveSection] = useState('search');
  const [adjacentSlugs, setAdjacentSlugs] = useState({
    prev: null,
    next: null,
    ready: false,
  });

  // Fetch carousel JS in parallel with course data (same module as React.lazy below).
  useEffect(() => {
    if (!courseSlug) return;
    void import(
      /* webpackChunkName: "course-image-carousel" */
      './CourseImageCarousel'
    );
  }, [courseSlug]);

  const handleAnchorClick = (event, href, sectionId) => {
    event.preventDefault(); // Prevent the default behavior of the anchor click
    setActiveSection(sectionId); // Set the active section state
    const element = document.querySelector(href);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 100, // Scroll to the element's position minus 100px
        behavior: 'smooth',
      });
    }
  };

  const fetchCourse = async () => {
    const { data } = await supabase
      .from('golf_courses')
      .select('*')
      .eq('slug_course_name', courseSlug)
      .maybeSingle();
    if (data) setCourse(data);
  };

  // Phase 1a: load golf_courses without `about` (large HTML) — fast first paint + smaller JSON
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setCourse(null);
    setClubCourseMapping(null);
    setGreenFees(null);
    setReciprocalsClean(null);
    setCourseImages(null);
    setGolfClubs(null);
    setAdjacentSlugs({ prev: null, next: null, ready: false });

    (async () => {
      try {
        const { data: row, error } = await supabase
          .from('golf_courses')
          .select(GOLF_COURSE_SELECT_NO_ABOUT)
          .eq('slug_course_name', courseSlug)
          .maybeSingle();

        if (cancelled) return;
        if (error) throw error;

        setCourse(row ?? null);
      } catch (err) {
        if (!cancelled) {
          console.log('Data fetching error: ', err);
          setCourse(null);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [courseSlug]);

  // Compute previous/next course within the same country ranking (wrap-around).
  useEffect(() => {
    if (!course?.course_country || !courseSlug) return;

    const rankField = getRankFieldForCountry(course.course_country);
    const country = course.course_country;

    let cancelled = false;
    (async () => {
      try {
        const { data: rows, error } = await supabase
          .from('golf_courses')
          .select(`slug_course_name, ${rankField}`)
          .eq('course_country', country)
          .not(rankField, 'is', null)
          .order(rankField, { ascending: true })
          .limit(2500);

        if (cancelled) return;
        if (error) throw error;

        const list = (rows || []).filter((r) => r?.slug_course_name);
        if (list.length === 0) {
          setAdjacentSlugs({ prev: null, next: null, ready: true });
          return;
        }

        const idx = list.findIndex((r) => r.slug_course_name === courseSlug);
        if (idx === -1) {
          // Current course is unranked or not in list; skip navigation among ranked ones.
          setAdjacentSlugs({ prev: null, next: null, ready: true });
          return;
        }

        const prev =
          list[(idx - 1 + list.length) % list.length]?.slug_course_name ?? null;
        const next = list[(idx + 1) % list.length]?.slug_course_name ?? null;
        setAdjacentSlugs({ prev, next, ready: true });
      } catch (e) {
        if (!cancelled) {
          console.error('Prev/Next course lookup failed:', e);
          setAdjacentSlugs({ prev: null, next: null, ready: true });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [course?.course_country, courseSlug]);

  const goPrevCourse = () => {
    if (!adjacentSlugs.prev) return;
    navigate(`/lists/golf-courses/${adjacentSlugs.prev}`);
  };

  const goNextCourse = () => {
    if (!adjacentSlugs.next) return;
    navigate(`/lists/golf-courses/${adjacentSlugs.next}`);
  };

  // Phase 1b: load `about` in a follow-up query (does not block shell / main column data)
  useEffect(() => {
    if (!courseSlug || !course?.course_uid) return;
    if (course.about !== undefined) return;

    let cancelled = false;
    const uid = course.course_uid;

    (async () => {
      try {
        const { data, error } = await supabase
          .from('golf_courses')
          .select('about')
          .eq('slug_course_name', courseSlug)
          .maybeSingle();

        if (cancelled) return;
        if (error) {
          console.log('About fetch error: ', error);
          return;
        }

        setCourse((prev) => {
          if (!prev || prev.course_uid !== uid) return prev;
          if (prev.about !== undefined) return prev;
          return { ...prev, about: data?.about ?? null };
        });
      } catch (err) {
        if (!cancelled) console.log('About fetch error: ', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [courseSlug, course?.course_uid, course?.about]);

  // Phase 2: secondary tables in parallel after we have a course row (deps avoid re-fetch when `about` merges)
  useEffect(() => {
    if (!courseSlug || !course?.course_uid) return;

    let cancelled = false;

    (async () => {
      try {
        const [
          { data: mappingRow },
          { data: greenFeesRow },
          { data: reciprocalsRow },
          { data: imagesRow },
        ] = await Promise.all([
          supabase
            .from('club_course_mapping')
            .select(CLUB_COURSE_MAPPING_SELECT)
            .eq('slug_course_name', courseSlug)
            .limit(1)
            .maybeSingle(),
          supabase
            .from('green_fees')
            .select('*')
            .eq('slug_course_name', courseSlug)
            .limit(1)
            .maybeSingle(),
          supabase
            .from('reciprocals_clean')
            .select('*')
            .eq('slug_course_name', courseSlug)
            .limit(1)
            .maybeSingle(),
          supabase
            .from('course_images')
            .select('*')
            .eq('slug_course_name', courseSlug)
            .limit(1)
            .maybeSingle(),
        ]);

        if (cancelled) return;

        setClubCourseMapping(mappingRow ?? null);
        setGreenFees(greenFeesRow ?? null);
        setReciprocalsClean(reciprocalsRow ?? null);
        setCourseImages(imagesRow ?? null);

        if (mappingRow) {
          const clubUIDs = mappingRow.club_uid;
          const { data: clubData } = await supabase
            .from('golf_clubs')
            .select(GOLF_CLUB_SELECT_PAGE)
            .in('club_uid', Array.isArray(clubUIDs) ? clubUIDs : [clubUIDs]);
          if (!cancelled) setGolfClubs(clubData);
        } else {
          setGolfClubs(null);
        }
      } catch (err) {
        console.log('Secondary fetch error: ', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [courseSlug, course?.course_uid]);

  // Netlify Prerender extension: defer snapshot until shell/data finished (avoids mixed head/body states).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.prerenderReady = false;
  }, [courseSlug]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isLoading) return;
    let cancelled = false;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!cancelled) window.prerenderReady = true;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [isLoading]);

  const pageUrl = `https://golflists.com${location.pathname}`;
  const label = slugToLabel(courseSlug);

  if (!isLoading && !course) {
    return (
      <>
        <MetaTags
          title="Golf Course Not Found | GolfLists"
          description="This golf course page could not be found on GolfLists."
          url={pageUrl}
          noindex
          statusCode={404}
        />
        <NotFound />
      </>
    );
  }

  let metaTitle = `${label} | Golf course | GolfLists`;
  let metaDescription = `${label}: UK & Ireland golf course guide — rankings, green fees, visitor info, architect and county rank on GolfLists.`;
  let metaKeywords = `${label}, golf course, UK golf, Ireland golf, GolfLists`;

  if (course) {
    metaTitle = `${course.course_name} - Golf Course Details`;
    metaKeywords = `${course.course_name}, golf course ${course.course_county || ''}, ${course.course_country || 'UK'} golf, green fees, golf rankings, GolfLists`;
    if (course.about) {
      const plain = stripHtmlForMeta(course.about);
      metaDescription =
        plain.slice(0, 160) ||
        `${course.course_name}: ranked #${course.county_rank ?? '—'} in ${course.course_county}, ${course.course_country}. Green fees, visitor info and course details on GolfLists.`;
    } else {
      metaDescription = `${course.course_name}: golf course in ${course.course_county}, ${course.course_country}. County rank #${course.county_rank ?? '—'}, green fees, visitor policy and architect info on GolfLists — UK & Ireland golf database.`;
    }
  }

  const courseSchema = course
    ? {
    '@context': 'https://schema.org',
    '@type': 'GolfCourse',
    name: course.course_name,
    description:
      course.about?.substring(0, 160) ||
      `${course.course_name} is a golf course in ${course.course_county}, ${course.course_country}.`,
    url: `https://golflists.com${location.pathname}`,
    ...(course.course_address && {
      address: {
        '@type': 'PostalAddress',
        streetAddress: course.course_address,
        addressLocality: course.course_city || course.course_county,
        addressRegion: course.course_county,
        addressCountry: course.course_country,
        postalCode: course.course_postcode,
      },
    }),
    ...(course.latitude &&
      course.longitude && {
        geo: {
          '@type': 'GeoCoordinates',
          latitude: course.latitude,
          longitude: course.longitude,
        },
      }),
    ...(course.number_holes && { numberOfHoles: course.number_holes }),
    ...(course.par && { par: course.par }),
    ...(courseImages?.course_image_array?.length > 0 && {
      image: courseImages.course_image_array
        .filter(Boolean)
        .slice(0, 12),
    }),
    ...(() => {
      const features = [];
      if (course.course_style) {
        features.push({
          '@type': 'LocationFeatureSpecification',
          name: 'Course Style',
          value: course.course_style,
        });
      }
      if (course.visitor_policy) {
        features.push({
          '@type': 'LocationFeatureSpecification',
          name: 'Visitor Policy',
          value: course.visitor_policy,
        });
      }
      return features.length ? { amenityFeature: features } : {};
    })(),
  }
    : null;

  return (
    <>
      <MetaTags
        title={metaTitle}
        description={metaDescription}
        imageUrl="https://stjbqlfgvlefqghlyumg.supabase.co/storage/v1/object/public/brand/GL_LOGO_300_302.png"
        url={pageUrl}
        keywords={metaKeywords}
      />

      <div className="bg-gray-100 mx-auto min-h-[50vh]">
        <GolfCourseNav
          handleAnchorClick={handleAnchorClick}
          activeSection={activeSection}
          onPrevCourse={goPrevCourse}
          onNextCourse={goNextCourse}
          prevDisabled={!adjacentSlugs.ready || !adjacentSlugs.prev}
          nextDisabled={!adjacentSlugs.ready || !adjacentSlugs.next}
        />
        <div className="mx-auto px-1 sm:px-6 xl:px-8">
          <ContributeBanner />
        </div>
        <div className="mx-auto px-1 sm:px-6 xl:px-8">
          {isLoading && <GolfCourseShellSkeleton label={label} />}

          {!isLoading && course && (
            <>
              {courseSchema && <JsonLd data={courseSchema} />}
              {/* Responsive layout: Stacked on mobile, grid on large screens */}
              <div className="grid grid-cols-1 xl:grid-cols-3 xl:gap-4">
            {/* Top Left: GolfCourseIntro */}
              <div className="flex flex-col flex-nowrap">
                <GolfCourseHeader course={course} />
                <div className="xl:hidden">
                  <GolfCourseRank course={course} />
                  <Suspense fallback={<CourseCarouselSkeleton />}>
                    <CourseImageCarousel
                      course={course}
                      courseImages={courseImages}
                    />
                  </Suspense>
                  <GolfCourseAbout 
                    course={course} 
                    user={user}
                    editableCourses={editableCourses}
                    editPermissions={editPermissions}
                    username={username}
                  />
                  <GolfCoursePar course={course} />
                </div>
                <GolfCourseDesign course={course} />
                <Suspense fallback={<CourseMapSkeleton />}>
                  <SingleCourseMap course={course} />
                </Suspense>
              </div>

            {/* Top Center: GolfCourseHeader */}
            <div className="flex flex-col flex-nowrap">
              <div className="xl:col-span-1">
                <div className="hidden xl:block">
                  <GolfCoursePar course={course} />
                  <GolfCourseAbout 
                    course={course} 
                    user={user}
                    editableCourses={editableCourses}
                    editPermissions={editPermissions}
                    username={username}
                  />
                </div>
              </div>
            </div>

            {/* Top Right: CourseImageCarousel */}
            <div className="flex flex-col flex-nowrap">
                <div className="hidden xl:block">
                  <GolfCourseRank course={course} />
                  <Suspense fallback={<CourseCarouselSkeleton />}>
                    <CourseImageCarousel
                      course={course}
                      courseImages={courseImages}
                    />
                  </Suspense>
                </div>
            </div>
          </div>

          {/* Middle Left: GolfCourseGreenFees */}

          <div className="grid grid-cols-1 xl:grid-cols-2 xl:gap-4">
            {/* Middle Left: VisitorInfo */}
            <Suspense
              fallback={<LoadingScreen type="spin" color="#000" />}
              className="xl:col-span-1"
            >
              <VisitorInfo
                user={user}
                editableCourses={editableCourses}
                editPermissions={editPermissions}
                course={course}
                clubCourseMapping={clubCourseMapping}
                greenFees={greenFees}
                reciprocalsClean={reciprocalsClean}
                golfClubs={golfClubs}
                fetchCourse={fetchCourse}
                username={username}
              />
            </Suspense>
            {/* Middle Right: VisitorInfo */}
            <Suspense
              fallback={<LoadingScreen type="spin" color="#000" />}
              className="xl:col-span-1"
            >
              <GolfCourseGreenFees
                course={course}
                clubCourseMapping={clubCourseMapping}
                greenFees={greenFees}
                reciprocalsClean={reciprocalsClean}
                golfClubs={golfClubs}
                user={user}
                editableCourses={editableCourses}
                editPermissions={editPermissions}
                username={username}
              />
            </Suspense>
          </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default GolfCourseOverview;
